import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
);

export default function AccountLogin() {
  const { user, login, register } = useCustomerAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (user) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back!" : "Account created — welcome to Nutribest!");
      navigate("/account", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-cream border border-forest/15 rounded-xl pl-10 pr-3.5 py-3 text-sm focus:outline-none focus:border-gold";

  return (
    <div data-testid="account-login-page" className="container-nb py-16 sm:py-24 flex justify-center">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="eyebrow mb-3">{mode === "login" ? "Welcome back" : "Join Nutribest"}</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-forest">{mode === "login" ? "Sign in to your account" : "Create your account"}</h1>
          <p className="text-sm text-mutedgreen mt-3">Track orders, save your address and review faster.</p>
        </div>

        <button data-testid="google-login-button" onClick={googleLogin} className="w-full flex items-center justify-center gap-3 bg-white border border-forest/20 rounded-full py-3 text-sm font-semibold text-forest hover:bg-forest/5 transition-colors">
          <GoogleIcon /> Continue with Google
        </button>

        <div className="flex items-center gap-4 my-6">
          <span className="flex-1 h-px bg-forest/10" />
          <span className="text-[11px] uppercase tracking-[0.16em] text-mutedgreen">or</span>
          <span className="flex-1 h-px bg-forest/10" />
        </div>

        <form data-testid="account-auth-form" onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div className="relative">
              <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mutedgreen" />
              <input data-testid="account-name-input" value={form.name} onChange={on("name")} placeholder="Full name" required className={inputCls} />
            </div>
          )}
          <div className="relative">
            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mutedgreen" />
            <input data-testid="account-email-input" type="email" value={form.email} onChange={on("email")} placeholder="Email address" required className={inputCls} />
          </div>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mutedgreen" />
            <input data-testid="account-password-input" type="password" value={form.password} onChange={on("password")} placeholder={mode === "login" ? "Password" : "Password (min 8 characters)"} required className={inputCls} />
          </div>
          <button type="submit" data-testid="account-submit-button" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream text-xs font-bold uppercase tracking-[0.16em] py-3.5 rounded-full hover:bg-gold transition-colors disabled:opacity-60">
            {busy && <Loader2 size={14} className="animate-spin" />} {mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="text-center text-sm text-mutedgreen mt-6">
          {mode === "login" ? "New to Nutribest? " : "Already have an account? "}
          <button data-testid="account-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-forest font-semibold hover:text-gold link-underline">
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </p>
        <p className="text-center text-[11px] text-mutedgreen mt-3">Mobile OTP sign-in coming soon. <Link to="/" className="link-underline">Back to store</Link></p>
      </div>
    </div>
  );
}
